import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getDocumentationById, getScreenshotUrl } from '../../../models/documentationModel';
import { API_BASE_URL } from '../../../config';

function DocumentationViewer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewStats, setViewStats] = useState(null);

  useEffect(() => {
    fetchDocumentation();
  }, [id]);

  const fetchDocumentation = async () => {
    try {
      setLoading(true);
      const response = await getDocumentationById(id);
      if (response.status === 1 || response.status === 'success') {
        setDoc(response.data?.documentation);
        setViewStats(response.data?.documentation?.view_stats);
      }
    } catch (error) {
      console.error('Error fetching documentation:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const isActive = status == 1;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${
        isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
      }`}>
        {isActive ? 'Active' : 'Draft'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeColors = {
      page: 'bg-blue-100 text-blue-800',
      tooltip: 'bg-yellow-100 text-yellow-800',
      walkthrough: 'bg-purple-100 text-purple-800',
      faq: 'bg-orange-100 text-orange-800'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${typeColors[type] || 'bg-gray-100 text-gray-800'}`}>
        {type}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800">Documentation not found</h2>
          <button
            onClick={() => navigate('/dashboard/documentation')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md"
          >
            Back to List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard/documentation')}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h1 className="text-xl font-bold text-gray-800">{doc.doc_title}</h1>
                  {getStatusBadge(doc.doc_status)}
                  {getTypeBadge(doc.doc_type)}
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>
                    <code className="bg-gray-100 px-2 py-0.5 rounded">{doc.doc_path}</code>
                  </span>
                  {doc.doc_section && (
                    <span>Section: {doc.doc_section}</span>
                  )}
                  {doc.author_name && (
                    <span>Author: {doc.author_name}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(`/dashboard/documentation/edit/${id}`)}
                className="px-4 py-2 rounded-md text-sm font-medium text-white"
                style={{ backgroundColor: '#0087D4' }}
              >
                <svg className="h-4 w-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Summary */}
            {doc.doc_summary && (
              <div className="bg-blue-50 border-l-4 border-blue-400 rounded-lg p-4 mb-4">
                <p className="text-blue-800">{doc.doc_summary}</p>
              </div>
            )}

            {/* Content */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div 
                className="prose max-w-none"
                dangerouslySetInnerHTML={{ __html: doc.doc_content }}
              />
            </div>

            {/* Screenshots Gallery */}
            {doc.screenshots && doc.screenshots.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Screenshots</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {doc.screenshots.map(screenshot => (
                    <div key={screenshot.screenshot_id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <a
                        href={`${API_BASE_URL}/api/documentation/screenshots/download/${screenshot.screenshot_filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={`${API_BASE_URL}/api/documentation/screenshots/download/${screenshot.screenshot_filename}`}
                          alt={screenshot.screenshot_caption || screenshot.screenshot_original_name}
                          className="w-full h-48 object-cover hover:opacity-90 transition-opacity"
                        />
                      </a>
                      {screenshot.screenshot_caption && (
                        <p className="p-2 text-sm text-gray-600 bg-gray-50">{screenshot.screenshot_caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Video Tutorial */}
            {doc.doc_video_url && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mt-4">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Video Tutorial</h2>
                <a
                  href={doc.doc_video_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  <svg className="h-5 w-5 mr-2 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                  Watch Video Tutorial
                </a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* View Stats */}
            {viewStats && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">View Statistics</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Total Views:</span>
                    <span className="font-medium">{viewStats.total_views || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Unique Viewers:</span>
                    <span className="font-medium">{viewStats.unique_viewers || 0}</span>
                  </div>
                  {viewStats.last_viewed && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last Viewed:</span>
                      <span className="font-medium">{new Date(viewStats.last_viewed).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium capitalize">{doc.doc_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Section:</span>
                  <span className="font-medium">{doc.doc_section || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Order:</span>
                  <span className="font-medium">{doc.doc_order}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="font-medium">{new Date(doc.created_at).toLocaleDateString()}</span>
                </div>
                {doc.updated_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Updated:</span>
                    <span className="font-medium">{new Date(doc.updated_at).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Keywords */}
            {doc.doc_keywords && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Keywords</h3>
                <div className="flex flex-wrap gap-1">
                  {doc.doc_keywords.split(',').map((keyword, i) => (
                    <span 
                      key={i} 
                      className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                    >
                      {keyword.trim()}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    const url = `${window.location.origin}${doc.doc_path}`;
                    navigator.clipboard.writeText(url);
                    alert('Path copied to clipboard!');
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                >
                  <svg className="h-4 w-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                  Copy Path
                </button>
                <button
                  onClick={() => window.open(doc.doc_path, '_blank')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md"
                >
                  <svg className="h-4 w-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Go to Path
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentationViewer;
